import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Pane, Heading, Text, Button, toaster, Spinner, CrossIcon, IconButton,
  Textarea, TrashIcon, SearchInput
} from 'evergreen-ui';
import api from '../../services/api';

const STATUS_LABELS = {
  not_tested: 'Not Tested',
  in_progress: 'In Progress',
  tested: 'Tested',
  vulnerable: 'Vulnerable',
};

const STATUS_DOT_COLORS = {
  not_tested: '#8F95B2',
  in_progress: '#3366FF',
  tested: '#52BD94',
  vulnerable: '#D14343',
};

const STATUS_BG_COLORS = {
  not_tested: '#F4F5F9',
  in_progress: '#F3F6FF',
  tested: '#F3FAF7',
  vulnerable: '#FDF4F4',
};

const METHOD_COLORS = {
  GET: '#61affe',
  POST: '#49cc90',
  PUT: '#fca130',
  DELETE: '#f93e3e',
  PATCH: '#50e3c2',
  HEAD: '#9012fe',
  OPTIONS: '#0d5aa7',
};

const SEVERITY_CONFIG = {
  critical: { color: '#D14343', bg: '#FDF4F4', label: 'Critical' },
  high: { color: '#EC4C47', bg: '#FEF6F6', label: 'High' },
  medium: { color: '#FFB020', bg: '#FFF8EC', label: 'Medium' },
  low: { color: '#47B881', bg: '#F1FAF5', label: 'Low' },
  info: { color: '#8F95B2', bg: '#F4F5F9', label: 'Info' },
};

const TECHNIQUE_COLORS = {
  'XSS': '#E53E3E',
  'Blind XSS': '#E53E3E',
  'SQL Injection': '#DD6B20',
  'Blind SQLi': '#DD6B20',
  'SSRF': '#805AD5',
  'Command Injection': '#C53030',
  'Path Traversal': '#D69E2E',
  'SSTI': '#B83280',
  'XXE': '#2B6CB0',
  'Open Redirect': '#38A169',
  'File Upload': '#3182CE',
  'Auth Bypass': '#E53E3E',
  'IDOR': '#DD6B20',
  'CSRF': '#805AD5',
};

function parseDescriptionData(descStr) {
  if (!descStr) return { techniques: [], request: null, response: null, params: [], statusCode: null, mimetype: null };
  try {
    const data = JSON.parse(descStr);
    return {
      techniques: data.techniques || [],
      request: data.request || null,
      response: data.response || null,
      params: data.params || [],
      statusCode: data.status_code || null,
      mimetype: data.mimetype || null,
    };
  } catch {
    // Not JSON, treat as plain text description
    return { techniques: [], request: null, response: null, params: [], statusCode: null, mimetype: null };
  }
}

const TABS = ['Details', 'Screenshots', 'Findings'];

const SectionHeader = ({ title, count, children }) => (
  <Pane
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    paddingY={10}
    paddingX={4}
    borderBottom="1px solid #E6E8F0"
    marginBottom={12}
  >
    <Pane display="flex" alignItems="center" gap={8}>
      <Text size={300} fontWeight={600} color="#474D66" textTransform="uppercase" letterSpacing="0.5px">
        {title}
      </Text>
      {count !== undefined && (
        <Pane
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          height={18}
          minWidth={18}
          paddingX={5}
          borderRadius={9}
          background="#E6E8F0"
        >
          <Text size={200} fontWeight={600} color="#696F8C">{count}</Text>
        </Pane>
      )}
    </Pane>
    {children}
  </Pane>
);

const CoverageNodeDetail = ({ node, projectId, canWrite, onClose, onRefresh }) => {
  const [findings, setFindings] = useState([]);
  const [projectFindings, setProjectFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('Details');
  const [findingSearch, setFindingSearch] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const fileInputRef = useRef(null);
  const pasteAreaRef = useRef(null);

  const fetchLinkedData = useCallback(async () => {
    if (!node || !projectId) return;
    setLoading(true);
    try {
      const findingsRes = await api.get(`/projects/${projectId}/findings`);
      const allFindings = findingsRes.data?.items || findingsRes.data || [];
      setProjectFindings(allFindings);
      const linkedFindingIds = new Set(node.finding_ids || []);
      setFindings(allFindings.filter(f => linkedFindingIds.has(f.id)));
    } catch (err) {
      toaster.danger('Failed to load linked data');
    } finally {
      setLoading(false);
    }
  }, [node, projectId]);

  useEffect(() => { fetchLinkedData(); }, [fetchLinkedData]);

  useEffect(() => {
    if (node) {
      setNotes(node.notes || '');
      setScreenshots(node.screenshots || []);
    }
  }, [node]);

  const handleLinkFinding = async (findingId) => {
    try {
      await api.post(`/projects/${projectId}/coverage/${node.id}/link-finding/${findingId}`);
      toaster.success('Finding linked');
      onRefresh();
    } catch (err) {
      toaster.danger(err.response?.data?.detail || 'Failed to link finding');
    }
  };

  const handleUnlinkFinding = async (findingId) => {
    try {
      await api.delete(`/projects/${projectId}/coverage/${node.id}/unlink-finding/${findingId}`);
      toaster.success('Finding unlinked');
      onRefresh();
    } catch (err) {
      toaster.danger(err.response?.data?.detail || 'Failed to unlink finding');
    }
  };

  const handleSaveNotes = async () => {
    try {
      await api.put(`/projects/${projectId}/coverage/${node.id}`, { notes });
      toaster.success('Notes saved');
      setEditingNotes(false);
      onRefresh();
    } catch (err) {
      toaster.danger('Failed to save notes');
    }
  };

  const handleScreenshotUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(
        `/projects/${projectId}/coverage/${node.id}/screenshots`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toaster.success('Screenshot uploaded');
      onRefresh();
    } catch (err) {
      toaster.danger('Failed to upload screenshot');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteScreenshot = async (screenshotId) => {
    try {
      await api.delete(`/projects/${projectId}/coverage/${node.id}/screenshots/${screenshotId}`);
      toaster.success('Screenshot removed');
      onRefresh();
    } catch (err) {
      toaster.danger('Failed to remove screenshot');
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        e.preventDefault();
        const file = items[i].getAsFile();
        if (file) await handleScreenshotUpload(file);
        return;
      }
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        if (files[i].type.startsWith('image/')) {
          await handleScreenshotUpload(files[i]);
        }
      }
    }
  };

  useEffect(() => {
    const el = pasteAreaRef.current;
    if (el) {
      el.addEventListener('paste', handlePaste);
      return () => el.removeEventListener('paste', handlePaste);
    }
  });

  if (!node) return null;

  const descData = parseDescriptionData(node.description);
  const hasReqRes = !!(descData.request || descData.response);
  const tabs = hasReqRes ? ['Details', 'Request/Response', 'Screenshots', 'Findings'] : TABS;

  const linkedFindingIds = new Set(node.finding_ids || []);
  const unlinkableFindings = projectFindings.filter(f => {
    if (linkedFindingIds.has(f.id)) return false;
    if (findingSearch.trim()) {
      const q = findingSearch.toLowerCase();
      return (f.name || '').toLowerCase().includes(q) || (f.title || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <Pane
      ref={pasteAreaRef}
      tabIndex={0}
      height="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {/* Header */}
      <Pane padding={16} borderBottom="1px solid #E6E8F0" flexShrink={0}>
        <Pane display="flex" alignItems="flex-start" justifyContent="space-between" marginBottom={12}>
          <Pane flex={1} marginRight={8}>
            <Heading size={500} marginBottom={6}>{node.name}</Heading>
            <Pane display="flex" gap={6} flexWrap="wrap" alignItems="center">
              {/* Status pill */}
              <Pane
                display="inline-flex"
                alignItems="center"
                gap={6}
                paddingX={8}
                paddingY={3}
                borderRadius={12}
                background={STATUS_BG_COLORS[node.status] || '#F4F5F9'}
              >
                <Pane
                  width={7}
                  height={7}
                  borderRadius="50%"
                  backgroundColor={STATUS_DOT_COLORS[node.status] || '#8F95B2'}
                />
                <Text size={200} fontWeight={500} color={STATUS_DOT_COLORS[node.status] || '#8F95B2'}>
                  {STATUS_LABELS[node.status]}
                </Text>
              </Pane>
              {/* HTTP method badge */}
              {node.http_method && (
                <Pane
                  paddingX={8}
                  paddingY={2}
                  borderRadius={3}
                  style={{
                    backgroundColor: METHOD_COLORS[node.http_method] || '#888',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    letterSpacing: '0.5px',
                  }}
                >
                  {node.http_method}
                </Pane>
              )}
            </Pane>
          </Pane>
          <IconButton
            icon={CrossIcon}
            appearance="minimal"
            onClick={onClose}
            style={{ marginTop: -4, marginRight: -4 }}
          />
        </Pane>

        {/* Endpoint URL card */}
        {node.url && (
          <Pane
            padding={10}
            background="#F9FAFC"
            borderRadius={6}
            border="1px solid #E6E8F0"
            marginBottom={4}
          >
            <Pane display="flex" alignItems="center" gap={6} marginBottom={2}>
              {node.http_method && (
                <Text
                  size={200}
                  fontWeight={700}
                  fontFamily="mono"
                  color={METHOD_COLORS[node.http_method] || '#888'}
                >
                  {node.http_method}
                </Text>
              )}
              <Text size={200} color="muted">Endpoint</Text>
            </Pane>
            <Text fontFamily="mono" size={300} wordBreak="break-all" color="#474D66">
              {node.url}
            </Text>
          </Pane>
        )}

        {/* Show param count badge in header if params exist */}
        {descData.params.length > 0 && (
          <Pane display="flex" gap={4} flexWrap="wrap" marginTop={8}>
            {descData.params.map((p, i) => (
              <Pane key={i} paddingX={6} paddingY={2} borderRadius={4} background={p.type === 'numeric' ? '#FFF8EC' : '#F4F5F9'}>
                <Text size={200} fontFamily="mono" fontWeight={500} color={p.type === 'numeric' ? '#DD6B20' : '#696F8C'}>
                  {p.in === 'path' ? `{${p.name}}` : `?${p.name}`}
                </Text>
              </Pane>
            ))}
          </Pane>
        )}
      </Pane>

      {/* Tab bar */}
      <Pane
        display="flex"
        borderBottom="1px solid #E6E8F0"
        paddingX={16}
        flexShrink={0}
        background="white"
      >
        {tabs.map(tab => {
          const isActive = activeTab === tab;
          let count = null;
          if (tab === 'Screenshots') count = screenshots.length;
          if (tab === 'Findings') count = findings.length;
          return (
            <Pane
              key={tab}
              paddingX={14}
              paddingY={10}
              cursor="pointer"
              onClick={() => setActiveTab(tab)}
              borderBottom={isActive ? '2px solid #3366FF' : '2px solid transparent'}
              marginBottom={-1}
              display="flex"
              alignItems="center"
              gap={6}
              style={{ transition: 'border-color 0.15s ease' }}
            >
              <Text
                size={300}
                fontWeight={isActive ? 600 : 400}
                color={isActive ? '#3366FF' : '#696F8C'}
              >
                {tab}
              </Text>
              {count !== null && count > 0 && (
                <Pane
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  height={16}
                  minWidth={16}
                  paddingX={4}
                  borderRadius={8}
                  background={isActive ? '#3366FF' : '#E6E8F0'}
                >
                  <Text size={200} fontWeight={600} color={isActive ? '#fff' : '#696F8C'}>
                    {count}
                  </Text>
                </Pane>
              )}
            </Pane>
          );
        })}
      </Pane>

      {/* Tab content */}
      <Pane flex={1} overflow="auto" padding={16}>
        {loading ? (
          <Pane display="flex" justifyContent="center" padding={32}><Spinner /></Pane>
        ) : (
          <>
            {/* === DETAILS TAB === */}
            {activeTab === 'Details' && (
              <Pane>
                {/* Technique badges */}
                {descData.techniques.length > 0 && (
                  <Pane marginBottom={16}>
                    <SectionHeader title="Detected Techniques" count={descData.techniques.length} />
                    <Pane display="flex" gap={6} flexWrap="wrap">
                      {descData.techniques.map(tech => {
                        const color = TECHNIQUE_COLORS[tech] || '#718096';
                        return (
                          <Pane
                            key={tech}
                            paddingX={10}
                            paddingY={4}
                            borderRadius={6}
                            style={{
                              backgroundColor: color + '15',
                              border: `1px solid ${color}40`,
                            }}
                          >
                            <Text size={300} fontWeight={600} color={color}>{tech}</Text>
                          </Pane>
                        );
                      })}
                    </Pane>
                  </Pane>
                )}

                {/* Parameters */}
                {descData.params.length > 0 && (
                  <Pane marginBottom={16}>
                    <SectionHeader title="Parameters" count={descData.params.length} />
                    <Pane display="flex" flexDirection="column" gap={4}>
                      {descData.params.map((p, i) => (
                        <Pane
                          key={i}
                          display="flex"
                          alignItems="center"
                          gap={8}
                          paddingX={10}
                          paddingY={6}
                          background="#F9FAFC"
                          borderRadius={6}
                          border="1px solid #E6E8F0"
                        >
                          <Pane
                            paddingX={6}
                            paddingY={1}
                            borderRadius={3}
                            background={p.in === 'path' ? '#EBF0FF' : '#F0FFF4'}
                          >
                            <Text size={200} fontWeight={600} color={p.in === 'path' ? '#3366FF' : '#38A169'} fontFamily="mono">
                              {p.in}
                            </Text>
                          </Pane>
                          <Text size={300} fontWeight={600} fontFamily="mono" color="#474D66">{p.name}</Text>
                          {p.type === 'numeric' && (
                            <Pane paddingX={5} paddingY={1} borderRadius={3} background="#FFF8EC">
                              <Text size={200} fontWeight={600} color="#DD6B20">numeric</Text>
                            </Pane>
                          )}
                          {p.value && (
                            <Text size={200} color="#8F95B2" fontFamily="mono" flex={1} style={{ textAlign: 'right' }}>
                              {p.value.length > 30 ? p.value.slice(0, 30) + '...' : p.value}
                            </Text>
                          )}
                        </Pane>
                      ))}
                    </Pane>
                  </Pane>
                )}

                {/* Quick status info */}
                {node.assigned_user_id && (
                  <Pane marginBottom={16}>
                    <Text size={200} color="muted" display="block" marginBottom={4}>Assigned To</Text>
                    <Text size={300} color="#474D66">{node.assigned_user_id}</Text>
                  </Pane>
                )}

                <SectionHeader title="Notes" />
                {canWrite ? (
                  <Pane marginBottom={16}>
                    {editingNotes ? (
                      <>
                        <Textarea
                          value={notes}
                          onChange={e => setNotes(e.target.value)}
                          placeholder="Add notes, observations, test results..."
                          rows={8}
                          marginBottom={8}
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 13,
                            lineHeight: '1.5',
                            border: '1px solid #D8DAE5',
                            borderRadius: 6,
                          }}
                        />
                        <Pane display="flex" gap={8}>
                          <Button size="small" appearance="primary" onClick={handleSaveNotes}>
                            Save Notes
                          </Button>
                          <Button
                            size="small"
                            onClick={() => { setEditingNotes(false); setNotes(node.notes || ''); }}
                          >
                            Cancel
                          </Button>
                        </Pane>
                      </>
                    ) : (
                      <Pane
                        padding={12}
                        background="#F9FAFC"
                        borderRadius={6}
                        cursor="pointer"
                        onClick={() => setEditingNotes(true)}
                        minHeight={48}
                        border="1px solid #E6E8F0"
                        style={{
                          transition: 'border-color 0.15s ease',
                        }}
                        className="notes-hover"
                      >
                        {notes ? (
                          <Text size={300} whiteSpace="pre-wrap" fontFamily="mono" color="#474D66">
                            {notes}
                          </Text>
                        ) : (
                          <Pane display="flex" alignItems="center" justifyContent="center" padding={12}>
                            <Text size={300} color="#8F95B2">Click to add notes...</Text>
                          </Pane>
                        )}
                      </Pane>
                    )}
                  </Pane>
                ) : (
                  notes ? (
                    <Pane
                      padding={12}
                      background="#F9FAFC"
                      borderRadius={6}
                      marginBottom={16}
                      border="1px solid #E6E8F0"
                    >
                      <Text size={300} whiteSpace="pre-wrap" fontFamily="mono" color="#474D66">
                        {notes}
                      </Text>
                    </Pane>
                  ) : (
                    <Text size={300} color="#8F95B2" display="block" marginBottom={16}>
                      No notes yet
                    </Text>
                  )
                )}
              </Pane>
            )}

            {/* === REQUEST/RESPONSE TAB === */}
            {activeTab === 'Request/Response' && (
              <Pane>
                {/* Response status line */}
                {descData.statusCode && (
                  <Pane display="flex" gap={8} alignItems="center" marginBottom={16}>
                    <Pane paddingX={10} paddingY={4} borderRadius={6} background={
                      descData.statusCode.startsWith('2') ? '#F0FFF4' :
                      descData.statusCode.startsWith('3') ? '#EBF0FF' :
                      descData.statusCode.startsWith('4') ? '#FFF8EC' : '#FDF4F4'
                    }>
                      <Text fontFamily="mono" fontWeight={700} size={400} color={
                        descData.statusCode.startsWith('2') ? '#38A169' :
                        descData.statusCode.startsWith('3') ? '#3366FF' :
                        descData.statusCode.startsWith('4') ? '#DD6B20' : '#E53E3E'
                      }>
                        {descData.statusCode}
                      </Text>
                    </Pane>
                    {descData.mimetype && (
                      <Text size={300} color="#696F8C" fontFamily="mono">{descData.mimetype}</Text>
                    )}
                  </Pane>
                )}

                {descData.request && (
                  <Pane marginBottom={20}>
                    <SectionHeader title="Request" />
                    <Pane
                      padding={14}
                      background="#1E1E2E"
                      borderRadius={8}
                      overflow="auto"
                      maxHeight={400}
                      border="1px solid #2D2D3F"
                    >
                      <Text
                        fontFamily="mono"
                        size={300}
                        color="#CDD6F4"
                        whiteSpace="pre-wrap"
                        style={{ lineHeight: '1.6', wordBreak: 'break-all' }}
                      >
                        {descData.request}
                      </Text>
                    </Pane>
                  </Pane>
                )}

                {descData.response && (
                  <Pane marginBottom={20}>
                    <SectionHeader title="Response" />
                    <Pane
                      padding={14}
                      background="#1E1E2E"
                      borderRadius={8}
                      overflow="auto"
                      maxHeight={400}
                      border="1px solid #2D2D3F"
                    >
                      <Text
                        fontFamily="mono"
                        size={300}
                        color="#CDD6F4"
                        whiteSpace="pre-wrap"
                        style={{ lineHeight: '1.6', wordBreak: 'break-all' }}
                      >
                        {descData.response}
                      </Text>
                    </Pane>
                  </Pane>
                )}

                {!descData.request && !descData.response && (
                  <Pane textAlign="center" padding={32}>
                    <Text color="#8F95B2" size={300}>No request/response data available</Text>
                  </Pane>
                )}
              </Pane>
            )}

            {/* === SCREENSHOTS TAB === */}
            {activeTab === 'Screenshots' && (
              <Pane>
                {/* Upload area */}
                {canWrite && (
                  <Pane
                    padding={20}
                    background={dragOver ? '#EBF0FF' : '#F9FAFC'}
                    borderRadius={8}
                    border={dragOver ? '2px dashed #3366FF' : '2px dashed #D8DAE5'}
                    textAlign="center"
                    cursor="pointer"
                    onClick={() => fileInputRef.current?.click()}
                    marginBottom={16}
                    style={{ transition: 'all 0.2s ease' }}
                  >
                    {uploading ? (
                      <Pane display="flex" alignItems="center" justifyContent="center" gap={8}>
                        <Spinner size={16} />
                        <Text size={300} color="#696F8C">Uploading...</Text>
                      </Pane>
                    ) : (
                      <Pane>
                        <Text size={400} display="block" color="#474D66" marginBottom={4}>
                          Drop image here, click to upload, or paste (Ctrl+V)
                        </Text>
                        <Text size={200} color="#8F95B2">
                          Supports PNG, JPG, GIF, WebP
                        </Text>
                      </Pane>
                    )}
                  </Pane>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={e => {
                    if (e.target.files[0]) handleScreenshotUpload(e.target.files[0]);
                    e.target.value = '';
                  }}
                />

                {/* Screenshot gallery */}
                {screenshots.length > 0 ? (
                  <Pane display="flex" flexDirection="column" gap={12}>
                    {screenshots.map((s, idx) => (
                      <Pane
                        key={s.id || idx}
                        position="relative"
                        borderRadius={8}
                        overflow="hidden"
                        border="1px solid #E6E8F0"
                        style={{
                          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                          transition: 'box-shadow 0.2s ease',
                        }}
                      >
                        <img
                          src={s.thumbnail_url || s.url}
                          alt={s.description || `Screenshot ${idx + 1}`}
                          style={{
                            width: '100%',
                            display: 'block',
                            cursor: 'pointer',
                          }}
                          onClick={() => setLightboxImage(s.url)}
                        />
                        <Pane
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          padding={8}
                          background="#F9FAFC"
                          borderTop="1px solid #E6E8F0"
                        >
                          <Text size={200} color="#8F95B2">
                            {s.description || `Screenshot ${idx + 1}`}
                          </Text>
                          {canWrite && (
                            <IconButton
                              icon={TrashIcon}
                              intent="danger"
                              size="small"
                              appearance="minimal"
                              onClick={() => handleDeleteScreenshot(s.id)}
                            />
                          )}
                        </Pane>
                      </Pane>
                    ))}
                  </Pane>
                ) : (
                  !canWrite && (
                    <Pane textAlign="center" padding={24}>
                      <Text color="#8F95B2" size={300}>No screenshots attached</Text>
                    </Pane>
                  )
                )}
              </Pane>
            )}

            {/* === FINDINGS TAB === */}
            {activeTab === 'Findings' && (
              <Pane>
                {/* Linked findings */}
                <SectionHeader title="Linked" count={findings.length} />
                {findings.length > 0 ? (
                  <Pane display="flex" flexDirection="column" gap={6} marginBottom={20}>
                    {findings.map(f => {
                      const sev = SEVERITY_CONFIG[f.severity] || SEVERITY_CONFIG.info;
                      return (
                        <Pane
                          key={f.id}
                          display="flex"
                          alignItems="center"
                          padding={10}
                          borderRadius={6}
                          border="1px solid #E6E8F0"
                          background="white"
                        >
                          <Pane
                            width={4}
                            height={28}
                            borderRadius={2}
                            backgroundColor={sev.color}
                            marginRight={10}
                            flexShrink={0}
                          />
                          <Pane flex={1}>
                            <Text size={300} fontWeight={500} color="#474D66" display="block">
                              {f.name || f.title}
                            </Text>
                          </Pane>
                          <Pane
                            paddingX={8}
                            paddingY={2}
                            borderRadius={4}
                            background={sev.bg}
                            marginRight={8}
                          >
                            <Text size={200} fontWeight={600} color={sev.color}>
                              {sev.label}
                            </Text>
                          </Pane>
                          {canWrite && (
                            <Button
                              size="small"
                              appearance="minimal"
                              intent="danger"
                              onClick={() => handleUnlinkFinding(f.id)}
                            >
                              Unlink
                            </Button>
                          )}
                        </Pane>
                      );
                    })}
                  </Pane>
                ) : (
                  <Text color="#8F95B2" size={300} display="block" marginBottom={20}>
                    No findings linked to this node
                  </Text>
                )}

                {/* Link new finding */}
                {canWrite && unlinkableFindings.length > 0 && (
                  <Pane>
                    <SectionHeader title="Available Findings" count={unlinkableFindings.length} />
                    <SearchInput
                      placeholder="Search findings..."
                      value={findingSearch}
                      onChange={e => setFindingSearch(e.target.value)}
                      width="100%"
                      height={32}
                      marginBottom={8}
                    />
                    <Pane maxHeight={200} overflow="auto">
                      {unlinkableFindings.slice(0, 30).map(f => {
                        const sev = SEVERITY_CONFIG[f.severity] || SEVERITY_CONFIG.info;
                        return (
                          <Pane
                            key={f.id}
                            display="flex"
                            alignItems="center"
                            paddingY={6}
                            paddingX={8}
                            borderRadius={4}
                            cursor="pointer"
                            onClick={() => handleLinkFinding(f.id)}
                            style={{ transition: 'background 0.1s ease' }}
                          >
                            <Pane
                              width={6}
                              height={6}
                              borderRadius="50%"
                              backgroundColor={sev.color}
                              marginRight={8}
                              flexShrink={0}
                            />
                            <Text size={300} flex={1} color="#474D66">{f.name || f.title}</Text>
                            <Text size={200} color="#3366FF" fontWeight={500}>+ Link</Text>
                          </Pane>
                        );
                      })}
                      {unlinkableFindings.length > 30 && (
                        <Text size={200} color="#8F95B2" display="block" paddingX={8} paddingY={4}>
                          ...and {unlinkableFindings.length - 30} more
                        </Text>
                      )}
                    </Pane>
                  </Pane>
                )}
              </Pane>
            )}
          </>
        )}
      </Pane>

      {/* Lightbox overlay */}
      {lightboxImage && (
        <Pane
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          background="rgba(0,0,0,0.85)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={999}
          cursor="pointer"
          onClick={() => setLightboxImage(null)}
        >
          <img
            src={lightboxImage}
            alt="Screenshot"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              borderRadius: 8,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
            onClick={e => e.stopPropagation()}
          />
          <Pane position="absolute" top={16} right={16}>
            <IconButton
              icon={CrossIcon}
              appearance="minimal"
              size="large"
              onClick={() => setLightboxImage(null)}
              style={{ color: '#fff' }}
            />
          </Pane>
        </Pane>
      )}
    </Pane>
  );
};

export default CoverageNodeDetail;
